import { CoverPhoto } from "@/components/ui/CoverPhoto";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type Accent =
  | "burgundy"
  | "gold"
  | "ember"
  | "teal"
  | "plum"
  | "amber";

export const accentGradient: Record<Accent, string> = {
  burgundy: "from-brand-700 via-brand-600 to-brand-900",
  gold: "from-gold-300 via-gold-500 to-ember-500",
  ember: "from-ember-400 via-ember-500 to-brand-700",
  teal: "from-teal-500 via-emerald-600 to-brand-800",
  plum: "from-[#6d2f57] via-brand-700 to-brand-900",
  amber: "from-amber-400 via-ember-500 to-brand-700",
};

function toAccent(value: string): Accent {
  return (value in accentGradient ? value : "burgundy") as Accent;
}

/**
 * Brand-styled placeholder artwork. Cohesive across the site and trivially
 * swappable for real photography later - replace usages with <Image />.
 */
export function Cover({
  accent = "burgundy",
  icon: Icon,
  label,
  photo,
  flat = false,
  plain = false,
  fit,
  className,
  children,
}: {
  accent?: string;
  icon?: LucideIcon;
  label?: string;
  photo?: string;
  flat?: boolean;
  /** Show the photo as it is - no coloured background or tint over it. */
  plain?: boolean;
  /** How the photo fills the box (default: contain). */
  fit?: "contain" | "cover";
  className?: string;
  children?: React.ReactNode;
}) {
  const key = toAccent(accent);
  return (
    <div
      className={cn(
        "relative isolate overflow-hidden",
        plain ? "bg-white" : flat ? "bg-brand-700" : cn("bg-gradient-to-br", accentGradient[key]),
        className
      )}
      aria-hidden={label ? undefined : true}
    >
      {photo && <CoverPhoto src={photo} plain={plain} fit={fit} />}
      {/* globe / arc motif - echoes the logo */}
      <svg
        className={cn("absolute -right-10 -top-12 h-56 w-56 text-white/12", (photo || flat) && "hidden")}
        viewBox="0 0 200 200"
        fill="none"
      >
        <circle cx="100" cy="100" r="92" stroke="currentColor" strokeWidth="1.5" />
        <ellipse cx="100" cy="100" rx="42" ry="92" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 100h184M18 60h164M18 140h164" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      {/* soft top highlight */}
      <div className={cn("absolute inset-0 bg-[radial-gradient(120%_80%_at_15%_0%,rgba(255,255,255,0.22),transparent_55%)]", (photo || flat) && "hidden")} />
      {/* fine grid */}
      <div className={cn("absolute inset-0 opacity-[0.12] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:34px_34px]", (photo || flat) && "hidden")} />

      {Icon && !photo && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="h-16 w-16 text-white/85 drop-shadow-sm" strokeWidth={1.4} />
        </div>
      )}
      {label && (
        <span className="absolute bottom-3 left-4 text-xs font-semibold uppercase tracking-[0.18em] text-white/85">
          {label}
        </span>
      )}
      {children}
    </div>
  );
}
